import JSZip from 'jszip';
import { Project, Phase, Step, Artifact } from '../types';

/* ------------------------------------------------------------------ */
/*  App import / export                                                */
/*                                                                    */
/*  Where an assembly line round-trips as a single JSON file, an *app* (a */
/*  full Project with generated artifacts) round-trips as a ZIP that   */
/*  is both human-browsable and re-importable:                         */
/*                                                                    */
/*    app.manifest.json                  ← structure (no content)      */
/*    artifacts/<order>-<phase>/<step>/<artifact.ext>  ← real files    */
/*                                                                    */
/*  Export writes every artifact in its actual format (.md, .ts,       */
/*  .html, .svg, …) so the files can be edited offline and re-         */
/*  imported. Import reads each artifact's content back from its file  */
/*  (the file is the source of truth), reconstructs a portable spec,   */
/*  and POSTs it to the orchestrator, which creates a brand-new        */
/*  project owned by the importer.                                     */
/* ------------------------------------------------------------------ */

/** Current archive format version. Bump on a breaking manifest change. */
export const APP_ARCHIVE_VERSION = 1;

const MANIFEST_NAME = 'app.manifest.json';

/** One artifact as recorded in the manifest — metadata plus a pointer to its file. */
export interface AppArtifactEntry {
  name: string;
  language?: string;
  producedAt?: string;
  producedBy?: string;
  /** `base64` when the file holds raw binary bytes; absent/`utf8` for text. */
  encoding?: 'utf8' | 'base64';
  /** MIME type for binary artifacts, e.g. `image/png`. */
  mimeType?: string;
  /** ZIP-relative path to the file holding this artifact's content. */
  file: string;
}

export interface AppStepEntry {
  key: string;
  label: string;
  desc: string;
  skillRef?: string;
  dependsOn?: string[];
  status: Step['status'];
  version: number;
  runLabel?: string;
  rerunLabel?: string;
  metadata?: Record<string, unknown>;
  artifacts: AppArtifactEntry[];
}

export interface AppPhaseEntry {
  key: string;
  label: string;
  desc: string;
  order: number;
  version: number;
  steps: AppStepEntry[];
}

export interface AppManifest {
  /** Format marker — must equal {@link APP_ARCHIVE_VERSION}. */
  factoryApp: number;
  exportedAt: string;
  app: {
    idea: string;
    appName: string;
    metadata: Record<string, string>;
    target?: string;
    buildPath?: string;
    phases: AppPhaseEntry[];
  };
}

/**
 * The portable, re-importable shape the orchestrator's POST /projects/import
 * accepts. Same as a Project minus server-owned fields (id/createdAt/version),
 * with artifact content inlined (read back from the archive's files).
 */
export interface AppImportSpec {
  idea: string;
  appName: string;
  metadata: Record<string, string>;
  target?: string;
  buildPath?: string;
  phases: Array<Omit<Phase, 'version'> & { version?: number }>;
}

/** Slugify a label into a filesystem-safe folder segment. */
function slug(s: string, fallback: string): string {
  return (
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '') || fallback
  );
}

/**
 * Filesystem-safe base name for a downloaded app archive. Deliberately avoids
 * a `.app.zip` form: macOS Archive Utility strips the trailing `.zip`, and a
 * resulting `<name>.app` folder is treated by Finder as an application bundle.
 * We use `<name>-app.zip`, and the archive holds a single `<name>/` root
 * folder, so unzipping always yields one clean app directory.
 */
export function appFileName(name: string): string {
  return `${slug(name, 'app')}-app.zip`;
}

/**
 * De-duplicate a filename within a folder by appending a numeric suffix
 * before the extension (`a.md`, `a-1.md`, …). Mirrors download.ts.
 */
function dedupe(name: string, seen: Map<string, number>): string {
  const count = seen.get(name) ?? 0;
  seen.set(name, count + 1);
  if (count === 0) return name;
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)}-${count}${name.slice(dot)}` : `${name}-${count}`;
}

/**
 * Build a downloadable ZIP for a fully-loaded project: real artifact files in
 * a phase/step tree plus an {@link AppManifest} describing the structure.
 */
export async function toAppArchive(project: Project): Promise<Blob> {
  const zip = new JSZip();
  const phases: AppPhaseEntry[] = [];

  // Everything lives under a single root folder named after the app, so
  // unzipping always yields one clean `<app>/` directory rather than spilling
  // loose files into the current folder.
  const root = slug(project.appName, 'app');

  const orderedPhases = [...project.phases].sort((a, b) => a.order - b.order);
  orderedPhases.forEach((phase, pi) => {
    // `file` paths in the manifest are relative to the manifest's own folder
    // (no root prefix), so re-import still works if the root folder is renamed.
    const phaseDir = `artifacts/${String(phase.order ?? pi).padStart(2, '0')}-${slug(phase.key, `phase-${pi}`)}`;
    const stepEntries: AppStepEntry[] = phase.steps.map((step) => {
      const stepDir = `${phaseDir}/${slug(step.key, 'step')}`;
      const seen = new Map<string, number>();
      const artifactEntries: AppArtifactEntry[] = step.artifacts.map((a: Artifact) => {
        const fileName = dedupe(a.name, seen);
        const relPath = `${stepDir}/${fileName}`;
        // Binary artifacts carry base64 in `content`; write the decoded bytes so
        // the file in the ZIP is a real PNG/PDF/etc., not base64 text.
        zip.file(`${root}/${relPath}`, a.content, { base64: a.encoding === 'base64' });
        return {
          name: a.name,
          language: a.language,
          producedAt: a.producedAt,
          producedBy: a.producedBy,
          encoding: a.encoding,
          mimeType: a.mimeType,
          file: relPath,
        };
      });
      return {
        key: step.key,
        label: step.label,
        desc: step.desc,
        skillRef: step.skillRef,
        dependsOn: step.dependsOn,
        status: step.status,
        version: step.version,
        runLabel: step.runLabel,
        rerunLabel: step.rerunLabel,
        metadata: step.metadata,
        artifacts: artifactEntries,
      };
    });
    phases.push({
      key: phase.key,
      label: phase.label,
      desc: phase.desc,
      order: phase.order ?? pi,
      version: phase.version,
      steps: stepEntries,
    });
  });

  const manifest: AppManifest = {
    factoryApp: APP_ARCHIVE_VERSION,
    exportedAt: new Date().toISOString(),
    app: {
      idea: project.idea,
      appName: project.appName,
      metadata: project.metadata ?? {},
      target: project.target,
      buildPath: project.buildPath,
      phases,
    },
  };

  zip.file(`${root}/${MANIFEST_NAME}`, JSON.stringify(manifest, null, 2));
  zip.file(
    `${root}/README.txt`,
    [
      `${project.appName} — Atlassian App Factory export`,
      '',
      `Unzipping produces a single "${root}/" directory holding the whole app:`,
      'every generated artifact in its real format under artifacts/<phase>/<step>/,',
      'plus app.manifest.json describing the structure.',
      'Edit the files freely, then re-import the ZIP from "My Apps → Import app".',
      'Keep app.manifest.json alongside the artifacts/ folder for re-import to work.',
    ].join('\n'),
  );

  return zip.generateAsync({ type: 'blob' });
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Parse an exported app ZIP back into an {@link AppImportSpec}, throwing a
 * human-readable Error on anything malformed. Artifact content is read from
 * the real files (the file is authoritative — so offline edits round-trip),
 * not from the manifest.
 */
export async function parseAppArchive(file: File): Promise<AppImportSpec> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    throw new Error('File is not a valid ZIP archive.');
  }

  // The manifest sits inside the app's root folder (e.g. `my-app/app.manifest.json`).
  // Find it wherever it is, preferring the shallowest match, and resolve every
  // artifact path against its folder.
  const manifestMatches = zip.file(new RegExp(`(^|/)${MANIFEST_NAME}$`));
  const manifestFile = manifestMatches.sort((a, b) => a.name.length - b.name.length)[0];
  if (!manifestFile) {
    throw new Error(`Archive is missing ${MANIFEST_NAME}.`);
  }
  const base = manifestFile.name.slice(0, manifestFile.name.length - MANIFEST_NAME.length); // '' or 'my-app/'

  let manifest: AppManifest;
  try {
    manifest = JSON.parse(await manifestFile.async('string')) as AppManifest;
  } catch {
    throw new Error(`${MANIFEST_NAME} is not valid JSON.`);
  }

  if (!manifest || typeof manifest !== 'object' || typeof manifest.factoryApp !== 'number') {
    throw new Error('This ZIP is not an App Factory export.');
  }
  if (manifest.factoryApp > APP_ARCHIVE_VERSION) {
    throw new Error(
      `Archive format v${manifest.factoryApp} is newer than this app supports (v${APP_ARCHIVE_VERSION}). Update the app and retry.`,
    );
  }

  const app = manifest.app;
  if (!app || typeof app !== 'object') throw new Error('Manifest is missing its "app" section.');
  const appName = str(app.appName).trim();
  if (!appName) throw new Error('App "appName" is required in the manifest.');
  if (!Array.isArray(app.phases)) throw new Error('Manifest "app.phases" must be an array.');

  const phases: AppImportSpec['phases'] = [];
  for (const [pi, p] of app.phases.entries()) {
    if (!p || typeof p !== 'object') throw new Error(`Phase #${pi + 1} is not an object.`);
    const steps: Step[] = [];
    const rawSteps = Array.isArray(p.steps) ? p.steps : [];
    for (const [si, s] of rawSteps.entries()) {
      if (!s || typeof s !== 'object') {
        throw new Error(`Step #${si + 1} in phase "${str(p.key) || pi + 1}" is not an object.`);
      }
      const artifacts: Artifact[] = [];
      const rawArtifacts = Array.isArray(s.artifacts) ? s.artifacts : [];
      for (const a of rawArtifacts) {
        const entryFile = str(a?.file);
        // Prefer the path resolved against the manifest's folder; fall back to
        // the raw path for archives that stored it root-prefixed.
        const f = entryFile ? zip.file(base + entryFile) ?? zip.file(entryFile) : null;
        if (!f) {
          throw new Error(`Artifact "${str(a?.name) || entryFile}" references missing file "${entryFile}".`);
        }
        const isBinary = a.encoding === 'base64';
        artifacts.push({
          name: str(a.name) || entryFile.split('/').pop() || 'artifact',
          // Binary artifacts are read back as base64 to preserve raw bytes.
          content: isBinary ? await f.async('base64') : await f.async('string'),
          encoding: isBinary ? 'base64' : undefined,
          mimeType: typeof a.mimeType === 'string' ? a.mimeType : undefined,
          language: typeof a.language === 'string' ? a.language : undefined,
          producedAt: typeof a.producedAt === 'string' ? a.producedAt : undefined,
          producedBy: typeof a.producedBy === 'string' ? a.producedBy : undefined,
        });
      }
      steps.push({
        key: str(s.key),
        label: str(s.label),
        desc: str(s.desc),
        skillRef: typeof s.skillRef === 'string' ? s.skillRef : undefined,
        dependsOn: Array.isArray(s.dependsOn) ? (s.dependsOn as unknown[]).map(str).filter(Boolean) : undefined,
        status: (s.status as Step['status']) ?? 'pending',
        version: typeof s.version === 'number' ? s.version : 0,
        artifacts,
        runLabel: typeof s.runLabel === 'string' ? s.runLabel : undefined,
        rerunLabel: typeof s.rerunLabel === 'string' ? s.rerunLabel : undefined,
        metadata:
          s.metadata && typeof s.metadata === 'object' ? (s.metadata as Record<string, unknown>) : undefined,
      });
    }
    phases.push({
      key: str(p.key),
      label: str(p.label),
      desc: str(p.desc),
      order: typeof p.order === 'number' ? p.order : pi,
      inputs: [],
      outputs: [],
      version: typeof p.version === 'number' ? p.version : 0,
      steps,
    });
  }

  return {
    idea: str(app.idea),
    appName,
    metadata:
      app.metadata && typeof app.metadata === 'object'
        ? Object.fromEntries(Object.entries(app.metadata).map(([k, v]) => [k, str(v)]))
        : {},
    target: typeof app.target === 'string' ? app.target : undefined,
    buildPath: typeof app.buildPath === 'string' ? app.buildPath : undefined,
    phases,
  };
}

/** Trigger a browser download of a blob via a temporary anchor. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
